import React, { useEffect, useState } from "react";
import "./toast.css";

const listeners = new Set();
let nextId = 1;
const DEFAULT_DURATION_MS = 4000;

function emit(toast) {
  for (const listener of listeners) {
    try {
      listener(toast);
    } catch {
      // listeners must not throw
    }
  }
}

// Imperative API. Safe to call from anywhere (event handlers, async chains)
// without React context. Variants: "info" | "success" | "error".
export function notify(message, options = {}) {
  const text = String(message || "").trim();
  if (!text) return null;
  const id = nextId++;
  emit({
    id,
    message: text,
    variant: options.variant === "error" || options.variant === "success" ? options.variant : "info",
    durationMs: Number.isFinite(options.durationMs) ? options.durationMs : DEFAULT_DURATION_MS,
  });
  return id;
}

export const toast = {
  info: (message, options) => notify(message, { ...options, variant: "info" }),
  success: (message, options) => notify(message, { ...options, variant: "success" }),
  error: (message, options) => notify(message, { ...options, variant: "error" }),
};

export function Toaster() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const handler = (toastEntry) => {
      setItems((previous) => [...previous, toastEntry]);
      const timeoutId = setTimeout(() => {
        setItems((previous) => previous.filter((entry) => entry.id !== toastEntry.id));
      }, toastEntry.durationMs);
      toastEntry._timeoutId = timeoutId;
    };
    listeners.add(handler);
    return () => {
      listeners.delete(handler);
    };
  }, []);

  const dismiss = (id) => {
    setItems((previous) => {
      const target = previous.find((entry) => entry.id === id);
      if (target?._timeoutId) clearTimeout(target._timeoutId);
      return previous.filter((entry) => entry.id !== id);
    });
  };

  if (!items.length) return null;

  return (
    <div className="toaster" role="region" aria-label="Notifications">
      {items.map((entry) => (
        <div
          key={entry.id}
          className={`toaster__toast toaster__toast--${entry.variant}`}
          role={entry.variant === "error" ? "alert" : "status"}
          aria-live={entry.variant === "error" ? "assertive" : "polite"}
        >
          <span className="toaster__message">{entry.message}</span>
          <button
            type="button"
            className="toaster__dismiss"
            aria-label="Dismiss notification"
            onClick={() => dismiss(entry.id)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

// Test-only helper: clears all listeners between unit tests.
export function __resetToastListenersForTests() {
  listeners.clear();
}
